import { useEffect, useState } from 'react'
import './PaystackDonateModal.css'
import { Modal } from 'antd';
import confetti from 'canvas-confetti';

const CONFETTI_COLORS = ["#f15621", "#37402c", "#f3d956", "#f9b9b3", "#ece5d1"];

// Test key only. Swap for Pangolino's live Paystack public key when ready to go live.
const PAYSTACK_PUBLIC_KEY = "pk_test_277fdd25a4b7771246d85d790956aa08e992e11d";

export const AMOUNT_TIERS = [5000, 10000, 25000];

// Recurring amounts are limited to tiers with a matching Paystack Plan (no backend to create plans on the fly)
const RECURRING_PLANS: Record<number, string> = {
  5000: "PLN_x4dm9bjy5zig1f3",
  10000: "PLN_xbhpp7vggdw9ml0",
  25000: "PLN_e4rpzze7uikrrqi",
};

export const RECURRING_TIERS = AMOUNT_TIERS.filter((tier) => RECURRING_PLANS[tier]);

interface PaystackDonateModalProps {
  visible: boolean;
  onClose: () => void;
  initialFrequency?: "once" | "monthly";
  initialAmount?: number;
  initialCustomAmount?: string;
}

export function PaystackDonateModal({ visible, onClose, initialFrequency, initialAmount, initialCustomAmount }: PaystackDonateModalProps): JSX.Element {
  const [frequency, setFrequency] = useState<"once" | "monthly">("once");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(AMOUNT_TIERS[0]);
  const [customAmount, setCustomAmount] = useState("");
  const [status, setStatus] = useState<"form" | "processing" | "success">("form");
  const [error, setError] = useState("");

  // Re-sync the form to the caller's chosen frequency/amount each time the modal opens
  useEffect(() => {
    if (!visible) return;
    const nextFrequency = initialFrequency ?? "once";
    setFrequency(nextFrequency);
    setSelectedAmount(initialAmount ?? (nextFrequency === "monthly" ? RECURRING_TIERS[0] : AMOUNT_TIERS[0]));
    setCustomAmount(nextFrequency === "once" ? (initialCustomAmount ?? "") : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Fire confetti "side cannons" from both screen edges for 3s while the thank-you screen is up
  useEffect(() => {
    if (status !== "success") return;

    let active = true;
    const end = Date.now() + 3000;

    const frame = () => {
      if (!active || Date.now() > end) return;

      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: CONFETTI_COLORS,
        zIndex: 2000,
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: CONFETTI_COLORS,
        zIndex: 2000,
      });

      requestAnimationFrame(frame);
    };

    frame();

    return () => {
      active = false;
    };
  }, [status]);

  const tiers = frequency === "monthly" ? RECURRING_TIERS : AMOUNT_TIERS;
  const amount = frequency === "monthly" ? selectedAmount : (customAmount ? Number(customAmount) : selectedAmount);

  const handleClose = () => {
    setStatus("form");
    setError("");
    onClose();
  };

  const handleFrequencyChange = (next: "once" | "monthly") => {
    setFrequency(next);
    setCustomAmount("");
    setSelectedAmount(next === "monthly" ? RECURRING_TIERS[0] : AMOUNT_TIERS[0]);
  };

  const handleSubmit = () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!isAnonymous && !name) {
      setError("Please enter your name.");
      return;
    }
    if (!isAnonymous && !phone) {
      setError("Please enter your phone number.");
      return;
    }
    if (!amount || amount <= 0) {
      setError("Please choose or enter an amount.");
      return;
    }
    setError("");

    const planCode = frequency === "monthly" ? RECURRING_PLANS[amount] : undefined;

    const customFields = [{ display_name: "Anonymous", variable_name: "anonymous", value: isAnonymous ? "Yes" : "No" }];
    if (!isAnonymous) {
      customFields.push({ display_name: "Name", variable_name: "name", value: name });
      customFields.push({ display_name: "Phone Number", variable_name: "phone", value: phone });
      if (comment) {
        customFields.push({ display_name: "Comment", variable_name: "comment", value: comment });
      }
    }

    window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email,
      amount: amount * 100, // Paystack expects the amount in kobo
      currency: "NGN",
      ref: `pangolino_${Date.now()}`,
      ...(planCode ? { plan: planCode } : {}),
      metadata: { custom_fields: customFields },
      callback: () => {
        setStatus("processing");
        setTimeout(() => setStatus("success"), 4500);
      },
      onClose: () => {},
    }).openIframe();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={420}
      className="PaystackDonateModal"
      destroyOnClose
    >
      {status === "form" ? (
        <div className="PaystackDonateModal-Form">
          <h2>Fund the Frontline of Pangolin Conservation!</h2>
          <div className="PaystackDonateModal-Frequency">
            <button
              type="button"
              className={"PaystackDonateModal-FrequencyOption" + (frequency === "once" ? " PaystackDonateModal-FrequencyOption-Active" : "")}
              onClick={() => handleFrequencyChange("once")}
            >
              One-time
            </button>
            <button
              type="button"
              className={"PaystackDonateModal-FrequencyOption" + (frequency === "monthly" ? " PaystackDonateModal-FrequencyOption-Active" : "")}
              onClick={() => handleFrequencyChange("monthly")}
            >
              Monthly
            </button>
          </div>
          <div className="PaystackDonateModal-Tiers">
            {tiers.map((tier) => (
              <button
                key={tier}
                type="button"
                className={"PaystackDonateModal-Tier" + (selectedAmount === tier && !customAmount ? " PaystackDonateModal-Tier-Active" : "")}
                onClick={() => { setSelectedAmount(tier); setCustomAmount(""); }}
              >
                ₦{tier.toLocaleString()}
              </button>
            ))}
          </div>
          {frequency === "once" && (
            <input
              className="PaystackDonateModal-Input"
              type="number"
              placeholder="Custom amount (₦)"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
            />
          )}
          <label className="PaystackDonateModal-FieldLabel">
            Email <span className="PaystackDonateModal-Required">*</span>
          </label>
          <input
            className="PaystackDonateModal-Input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!isAnonymous && (
            <>
              <label className="PaystackDonateModal-FieldLabel">
                Name <span className="PaystackDonateModal-Required">*</span>
              </label>
              <input
                className="PaystackDonateModal-Input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <label className="PaystackDonateModal-FieldLabel">
                Phone Number <span className="PaystackDonateModal-Required">*</span>
              </label>
              <input
                className="PaystackDonateModal-Input"
                type="tel"
                placeholder="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <textarea
                className="PaystackDonateModal-Textarea"
                placeholder="Add a comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </>
          )}
          <label className="PaystackDonateModal-Checkbox">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
            />
            Donate anonymously
          </label>
          {error && <p className="PaystackDonateModal-Error">{error}</p>}
          <button type="button" className="PaystackDonateModal-Submit" onClick={handleSubmit}>
            Continue to payment
          </button>
        </div>
      ) : status === "processing" ? (
        <div className="PaystackDonateModal-Processing">
          <div className="PaystackDonateModal-Spinner" />
          <p>Confirming your gift...</p>
        </div>
      ) : (
        <div className="PaystackDonateModal-Success">
          <h2>Thank you!</h2>
          <p>Your generosity helps Pangolino protect pangolins across Nigeria.</p>
          <p className="PaystackDonateModal-SuccessNote">A confirmation email is on its way to your inbox.</p>
          <button type="button" className="PaystackDonateModal-Submit" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
    </Modal>
  )
}
